import { useState, useEffect } from 'react';
import {
  TextField,
  Autocomplete,
  InputAdornment,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Controller, Control, FieldValues, Path, RegisterOptions, useWatch } from 'react-hook-form';
import { geocodingService } from '@/features/search/services/geocodingService';
import { AddressSuggestion } from '@/features/search/types/search.types';

interface AddressAutocompleteFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  rules?: RegisterOptions<T>;
  maxLength?: number;
  onAddressSelected?: (suggestion: AddressSuggestion) => void;
  onCoordinatesChange?: (lat: number | undefined, lng: number | undefined) => void;
  id?: string;
  ariaLabel?: string;
}

/**
 * Reusable address autocomplete field component with OpenStreetMap integration.
 *
 * Features:
 * - Real-time address suggestions from OpenStreetMap Nominatim
 * - Debounced search (300ms)
 * - Free-solo mode for custom addresses
 * - Auto-geocoding when user types without selecting
 * - Loading state indicator
 * - Accessible and mobile-optimized
 * - Integrates with react-hook-form
 *
 * @example
 * ```tsx
 * <AddressAutocompleteField
 *   name="address"
 *   control={control}
 *   label="Address"
 *   placeholder="Enter your address"
 *   required
 *   error={!!errors.address}
 *   helperText={errors.address?.message}
 *   onCoordinatesChange={(lat, lng) => {
 *     setValue('latitude', lat);
 *     setValue('longitude', lng);
 *   }}
 * />
 * ```
 */
export function AddressAutocompleteField<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  required = false,
  error = false,
  helperText,
  rules,
  maxLength,
  onAddressSelected,
  onCoordinatesChange,
  id,
  ariaLabel,
}: Readonly<AddressAutocompleteFieldProps<T>>) {
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState<AddressSuggestion | null>(null);

  // Watch the form value to sync with inputValue
  const formValue = useWatch({ control, name });

  // Sync inputValue when form value changes (e.g., form reset)
  useEffect(() => {
    if (!formValue || formValue === '') {
      setInputValue('');
      setSelectedSuggestion(null);
      // Clear coordinates when address is cleared
      if (onCoordinatesChange) {
        onCoordinatesChange(undefined, undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValue]);

  // Geocode address when user finishes typing without selecting
  const geocodeAddress = async (address: string) => {
    if (!address || address.length < 3) {
      if (onCoordinatesChange) {
        onCoordinatesChange(undefined, undefined);
      }
      return;
    }

    try {
      const suggestions = await geocodingService.getAddressSuggestions(address);
      if (suggestions.length > 0 && suggestions[0].coordinates) {
        // Use the first (best) match
        const coords = suggestions[0].coordinates;
        if (onCoordinatesChange) {
          onCoordinatesChange(coords.lat, coords.lng);
        }
      }
    } catch (error) {
      console.error('Error geocoding address:', error);
    }
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (inputValue.length < 3) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const suggestions = await geocodingService.getAddressSuggestions(inputValue);
        setOptions(suggestions);
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  return (
    <Box>
      {label && (
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          {label} {required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
      )}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { onChange, value, ...field } }) => (
          <Autocomplete
            {...field}
            value={value || ''}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
              // Update inputValue for all reasons
              setInputValue(newInputValue);

              // If user is typing, update the form value immediately
              if (reason === 'input') {
                onChange(newInputValue);
              }

              // Reset states when input is cleared
              if (newInputValue === '') {
                setSelectedSuggestion(null);
                if (onCoordinatesChange) {
                  onCoordinatesChange(undefined, undefined);
                }
              }
            }}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue !== 'string') {
                // User selected a suggestion
                onChange(newValue.description);
                setInputValue(newValue.description);
                setSelectedSuggestion(newValue);
                // Call callback with full suggestion data (including coordinates)
                if (onAddressSelected) {
                  onAddressSelected(newValue);
                }
                if (onCoordinatesChange && newValue.coordinates) {
                  onCoordinatesChange(newValue.coordinates.lat, newValue.coordinates.lng);
                }
              } else if (typeof newValue === 'string') {
                // User entered custom text
                onChange(newValue);
                setInputValue(newValue);
                setSelectedSuggestion(null);
              } else {
                // Cleared
                onChange('');
                setInputValue('');
                setSelectedSuggestion(null);
                if (onCoordinatesChange) {
                  onCoordinatesChange(undefined, undefined);
                }
              }
            }}
            onBlur={() => {
              // If user typed address without selecting, geocode it
              if (inputValue && !selectedSuggestion) {
                geocodeAddress(inputValue);
              }
            }}
            options={options}
            getOptionLabel={option => (typeof option === 'string' ? option : option.description)}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === 'string' && typeof value === 'string') {
                return option === value;
              }
              if (typeof option !== 'string' && typeof value === 'string') {
                return option.description === value;
              }
              return false;
            }}
            filterOptions={x => x} // Disable built-in filtering since we fetch from API
            loading={loading}
            freeSolo
            slotProps={{
              popper: {
                placement: 'bottom-start',
                disablePortal: true,
                modifiers: [
                  { name: 'offset', options: { offset: [0, 0] } },
                  { name: 'flip', enabled: false },
                  { name: 'preventOverflow', enabled: false },
                ],
                sx: { zIndex: 998 },
              },
              paper: {
                elevation: 4,
                sx: { backgroundColor: 'background.paper', pt: 0 },
              },
            }}
            renderInput={params => (
              <TextField
                {...params}
                id={id}
                placeholder={placeholder}
                error={error}
                helperText={helperText}
                aria-label={ariaLabel || label}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  },
                  htmlInput: maxLength
                    ? {
                        ...params.inputProps,
                        maxLength,
                      }
                    : params.inputProps,
                }}
              />
            )}
          />
        )}
      />
    </Box>
  );
}
